import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserGroupIcon, MagnifyingGlassIcon, CalendarIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FidelityCustomers = () => {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [searchTerm, customers]);

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${API}/fidelity/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
      toast.error('Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    if (!searchTerm) {
      setFilteredCustomers(customers);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = customers.filter(customer => 
      customer.name.toLowerCase().includes(term) ||
      customer.surname.toLowerCase().includes(term) ||
      customer.email.toLowerCase().includes(term) ||
      customer.customer_code.includes(term) ||
      customer.phone.includes(term)
    );
    setFilteredCustomers(filtered);
  };

  const loadPointsHistory = async (customerCode) => {
    try {
      const response = await axios.get(`${API}/fidelity/points/history/${customerCode}`);
      setPointsHistory(response.data);
    } catch (error) {
      console.error('Errore caricamento storico:', error);
      toast.error('Errore nel caricamento dello storico');
    }
  };

  const handleViewDetails = (customer) => {
    setSelectedCustomer(customer);
    loadPointsHistory(customer.customer_code);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('it-IT');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento clienti...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Clienti Fidelity</h1>
        <p className="text-gray-600">Gestione carte fedeltà e clienti registrati</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Clienti Totali</p>
              <p className="text-3xl font-bold text-blue-600">{customers.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Punti Totali Emessi</p>
              <p className="text-3xl font-bold text-green-600">
                {customers.reduce((sum, c) => sum + c.points_balance, 0).toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Carte Attive</p>
              <p className="text-3xl font-bold text-purple-600">
                {customers.filter(c => c.is_active).length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Cerca per nome, cognome, email, telefono o codice carta..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          {searchTerm && (
            <p className="text-sm text-gray-600 mt-2">
              Trovati {filteredCustomers.length} risultati
            </p>
          )}
        </CardContent>
      </Card>

      {/* Customers List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredCustomers.map((customer) => (
          <Card key={customer.id} className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">
                      {customer.name} {customer.surname}
                    </h3>
                    <Badge variant={customer.is_active ? "default" : "secondary"}>
                      {customer.is_active ? "Attiva" : "Disattivata"}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-blue-600">Carta:</span>
                      <span className="font-mono">{customer.customer_code}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <EnvelopeIcon className="w-4 h-4" />
                      <span>{customer.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="w-4 h-4" />
                      <span>{customer.phone}</span>
                    </div>
                    {customer.birthday && (
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4" />
                        <span>Compleanno: {formatDate(customer.birthday)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-sm text-gray-600 mb-1">Saldo Punti</div>
                  <div className="text-2xl font-bold text-green-600">
                    {customer.points_balance.toLocaleString()}
                  </div>
                  <button
                    onClick={() => handleViewDetails(customer)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Vedi Dettagli →
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredCustomers.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center">
              <UserGroupIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nessun cliente trovato
              </h3>
              <p className="text-sm text-gray-600">
                {searchTerm ? 'Prova a modificare i criteri di ricerca' : 'Non ci sono ancora clienti registrati'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>
                    {selectedCustomer.name} {selectedCustomer.surname}
                  </CardTitle>
                  <CardDescription>
                    Carta N° {selectedCustomer.customer_code}
                  </CardDescription>
                </div>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Customer Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-3">Informazioni Cliente</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-medium">{selectedCustomer.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Telefono:</span>
                    <p className="font-medium">{selectedCustomer.phone}</p>
                  </div>
                  {selectedCustomer.whatsapp && (
                    <div>
                      <span className="text-gray-600">WhatsApp:</span>
                      <p className="font-medium">{selectedCustomer.whatsapp}</p>
                    </div>
                  )}
                  {selectedCustomer.birthday && (
                    <div>
                      <span className="text-gray-600">Compleanno:</span>
                      <p className="font-medium">{formatDate(selectedCustomer.birthday)}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-600">Registrato il:</span>
                    <p className="font-medium">{formatDate(selectedCustomer.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Saldo Punti:</span>
                    <p className="font-bold text-green-600 text-lg">{selectedCustomer.points_balance}</p>
                  </div>
                </div>
              </div>

              {/* Points History */}
              <div>
                <h4 className="font-medium mb-3">Storico Movimenti</h4>
                {pointsHistory.length === 0 ? (
                  <p className="text-sm text-gray-600 text-center py-8">
                    Nessun movimento registrato
                  </p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {pointsHistory.map((trans) => (
                      <div
                        key={trans.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-sm">{trans.description}</p>
                          <p className="text-xs text-gray-600">
                            {formatDateTime(trans.created_at)}
                          </p>
                        </div>
                        <div className={`text-lg font-bold ${trans.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {trans.amount > 0 ? '+' : ''}{trans.amount}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FidelityCustomers;
